import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { 
  CalendarDays, 
  Download, 
  RefreshCw, 
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Clock,
  Settings2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { 
  SmartFiltersPanel, 
  DEFAULT_SMART_FILTERS, 
  calculateMaxValues, 
  applySmartFilters,
  getSmartFilterRowClass 
} from "./SmartFilters";
import { toast } from "sonner";
import { format, subDays, addDays } from "date-fns";

// Format number with commas
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return "-";
  return Number(num).toLocaleString('en-IN', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Format volume with K/L/Cr
const formatVolume = (num) => {
  if (num === undefined || num === null || num === 0) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(1) + " K";
  return num.toLocaleString('en-IN');
};

// Format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return format(date, "HH:mm:ss");
};

// Format date only (for Date column)
const formatDateOnly = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return format(date, "dd-MMM"); // e.g., "20-Feb"
};

const formatDate = (date) => {
  return format(date, "yyyy-MM-dd");
};

// Calculate bar width percentage (for gradient bars)
const getBarWidth = (value, maxValue, minWidth = 0) => {
  if (!value || !maxValue || maxValue === 0) return minWidth;
  return Math.max(minWidth, Math.min(100, (value / maxValue) * 100));
};

// All available columns
const ALL_COLUMNS = [
  { key: 'symbol', label: 'Symbol', sortable: true, width: '140px' },
  { key: 'date', label: 'Date', sortable: true, width: '90px' },
  { key: 'last_price', label: 'LTP', sortable: true, width: '100px' },
  { key: 'change', label: 'Change', sortable: true, width: '100px' },
  { key: 'trade_pos', label: 'Agg %', sortable: true, width: '80px' },
  { key: 'spread', label: 'Spread', sortable: true, width: '80px' },
  { key: 'bid_qty', label: 'Bid Qty', sortable: true, width: '90px', hasBar: true, barColor: 'green' },
  { key: 'bid_price', label: 'Bid', sortable: true, width: '100px' },
  { key: 'ask_price', label: 'Ask', sortable: true, width: '100px' },
  { key: 'ask_qty', label: 'Ask Qty', sortable: true, width: '90px', hasBar: true, barColor: 'red' },
  { key: 'agg_buy', label: 'Agg Buy', sortable: true, width: '90px', hasBar: true, barColor: 'green' },
  { key: 'agg_sell', label: 'Agg Sell', sortable: true, width: '90px', hasBar: true, barColor: 'red' },
  { key: 'last_traded_quantity', label: 'LTQ', sortable: true, width: '80px', hasBar: true, barColor: 'blue' },
  { key: 'volume_traded', label: 'Volume', sortable: true, width: '100px', hasBar: true, barColor: 'purple' },
  { key: 'volume_change', label: 'Vol Chg', sortable: true, width: '90px', hasBar: true, barColor: 'dynamic' },
  { key: 'oi', label: 'OI', sortable: true, width: '100px', hasBar: true, barColor: 'purple' },
  { key: 'oi_change', label: 'OI Chg', sortable: true, width: '90px', hasBar: true, barColor: 'dynamic' },
  { key: 'oi_pct', label: 'OI %', sortable: true, width: '70px' },
  { key: 'timestamp', label: 'Time', sortable: true, width: '100px' },
];

// Calculate Trade Position % - where the trade occurred within the spread
const calculateTradePosition = (tick) => {
  const ltp = tick.last_price || tick.ltp || 0;
  const bid = tick.bid_price || 0;
  const ask = tick.ask_price || 0;
  const spread = ask - bid;
  
  if (spread <= 0 || bid <= 0 || ask <= 0) return null;
  
  return ((ltp - bid) / spread) * 100;
};

const DEFAULT_COLUMNS = ['symbol', 'date', 'last_price', 'trade_pos', 'spread', 'bid_qty', 'bid_price', 'ask_price', 'ask_qty', 'agg_buy', 'agg_sell', 'last_traded_quantity', 'volume_traded', 'oi_change', 'timestamp'];

// Value cell with optional bar gradient
const ValueCell = ({ value, maxValue, color, formatFn = formatNumber }) => {
  const barWidth = getBarWidth(Math.abs(value || 0), maxValue);
  const displayValue = formatFn ? formatFn(value) : value;
  
  const colorClasses = {
    green: 'bg-green-500/30',
    red: 'bg-red-500/30',
    blue: 'bg-blue-500/30',
    purple: 'bg-purple-500/30',
    yellow: 'bg-yellow-500/30',
    cyan: 'bg-cyan-500/30',
    orange: 'bg-orange-500/30'
  };
  
  const textColorClasses = {
    green: 'text-green-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
    cyan: 'text-cyan-400',
    orange: 'text-orange-400'
  };

  return (
    <div className="relative h-full flex items-center">
      {maxValue > 0 && (
        <div 
          className={`absolute inset-0 ${colorClasses[color] || 'bg-gray-500/20'}`}
          style={{ width: `${barWidth}%` }}
        />
      )}
      <span className={`relative z-10 px-2 font-mono text-xs ${textColorClasses[color] || 'text-white'}`}>
        {displayValue}
      </span>
    </div>
  );
};

// History Row Component with all columns and bar gradients
const HistoryRow = ({ tick, visibleColumns, maxValues, smartFilterClass = '' }) => {
  const isAggBuy = tick.agg_buy > 0;
  const isAggSell = tick.agg_sell > 0;
  // Smart filter class takes priority over agg buy/sell colors
  const rowBgClass = smartFilterClass || (isAggBuy ? "bg-green-900/10" : isAggSell ? "bg-red-900/10" : "");

  const getCellContent = (col) => {
    const value = tick[col.key];
    
    switch (col.key) {
      case 'symbol':
        return <span className="font-mono text-xs text-white font-medium truncate">{value}</span>;
      case 'date':
        return <span className="font-mono text-xs text-cyan-400">{formatDateOnly(tick.timestamp)}</span>;
      case 'last_price':
        return <span className="font-mono text-xs text-white">{formatNumber(value)}</span>;
      case 'change':
        const change = tick.change || 0;
        return <span className={`font-mono text-xs ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatNumber(change)}</span>;
      case 'trade_pos':
        const tradePos = calculateTradePosition(tick);
        if (tradePos === null) return <span className="text-[#52525B] text-xs">-</span>;
        
        const isExtreme = tradePos > 100 || tradePos < 0;
        const isBuyerAggressive = tradePos >= 80;
        const isSellerAggressive = tradePos <= 20;
        const isHeavyBuyer = tradePos >= 90;
        const isHeavySeller = tradePos <= 10;
        
        let bgClass = 'bg-[#1a1a1a]';
        let textClass = 'text-white';
        
        if (isHeavyBuyer || tradePos > 100) {
          bgClass = 'bg-green-600/40';
          textClass = 'text-green-300 font-bold';
        } else if (isBuyerAggressive) {
          bgClass = 'bg-green-600/25';
          textClass = 'text-green-400';
        } else if (isHeavySeller || tradePos < 0) {
          bgClass = 'bg-red-600/40';
          textClass = 'text-red-300 font-bold';
        } else if (isSellerAggressive) {
          bgClass = 'bg-red-600/25';
          textClass = 'text-red-400';
        }
        
        return (
          <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
            <span className={`${textClass} text-xs font-mono`}>
              {isExtreme && (tradePos > 100 ? '🔥' : '❄️')}{tradePos.toFixed(0)}%
            </span>
          </div>
        );
      case 'spread':
        return <span className="font-mono text-xs text-yellow-400">{formatNumber(tick.spread || 0)}</span>;
      case 'bid_qty':
        return <ValueCell value={value} maxValue={maxValues.bid_qty} color="green" formatFn={formatVolume} />;
      case 'bid_price':
        return <span className="font-mono text-xs text-green-400">{formatNumber(value)}</span>;
      case 'ask_price':
        return <span className="font-mono text-xs text-red-400">{formatNumber(value)}</span>;
      case 'ask_qty':
        return <ValueCell value={value} maxValue={maxValues.ask_qty} color="red" formatFn={formatVolume} />;
      case 'agg_buy':
        return isAggBuy ? <ValueCell value={value} maxValue={maxValues.agg_buy} color="green" formatFn={formatVolume} /> : <span className="text-[#52525B] text-xs">-</span>;
      case 'agg_sell':
        return isAggSell ? <ValueCell value={value} maxValue={maxValues.agg_sell} color="red" formatFn={formatVolume} /> : <span className="text-[#52525B] text-xs">-</span>;
      case 'last_traded_quantity':
        return <ValueCell value={value} maxValue={maxValues.last_traded_quantity} color="blue" formatFn={formatVolume} />;
      case 'volume_traded':
        return <ValueCell value={value} maxValue={maxValues.volume_traded} color="purple" formatFn={formatVolume} />;
      case 'volume_change':
        const volChg = tick.volume_change || 0;
        const volChgColor = volChg >= 0 ? 'cyan' : 'orange';
        return <ValueCell value={Math.abs(volChg)} maxValue={maxValues.volume_change} color={volChgColor} formatFn={(v) => (volChg >= 0 ? '+' : '-') + formatVolume(v)} />;
      case 'oi':
        return <ValueCell value={value} maxValue={maxValues.oi} color="purple" formatFn={formatVolume} />;
      case 'oi_change':
        const oiChg = tick.oi_change || 0;
        const oiChgColor = oiChg >= 0 ? 'green' : 'red';
        return <ValueCell value={Math.abs(oiChg)} maxValue={maxValues.oi_change} color={oiChgColor} formatFn={(v) => (oiChg >= 0 ? '+' : '-') + formatVolume(v)} />;
      case 'oi_pct':
        const oiPct = tick.oi_pct_change || 0;
        return <span className={`font-mono text-xs ${oiPct > 0 ? 'text-green-400' : oiPct < 0 ? 'text-red-400' : 'text-[#71717A]'}`}>{oiPct.toFixed(1)}%</span>;
      case 'timestamp':
        return <span className="font-mono text-xs text-[#71717A]">{formatTimestamp(tick.timestamp)}</span>;
      default:
        return <span className="font-mono text-xs text-white">{value ?? '-'}</span>;
    }
  };

  // Use flex layout for virtualized rows
  return (
    <div className={`flex border-b border-[#1F1F23] hover:bg-[#1a1a1a] h-full items-center ${rowBgClass}`}>
      {visibleColumns.map(col => (
        <div 
          key={col.key} 
          className="py-1 px-1 overflow-hidden" 
          style={{ width: col.width, minWidth: col.width, flexShrink: 0 }}
        >
          {getCellContent(col)}
        </div>
      ))}
    </div>
  );
};

export default function HistoryTab({ instruments }) {
  const [startDate, setStartDate] = useState(subDays(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [startTime, setStartTime] = useState("09:15");  // Market opens at 9:15 AM
  const [endTime, setEndTime] = useState("15:30");       // Market closes at 3:30 PM
  const [historyData, setHistoryData] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFull, setIsLoadingFull] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState("ALL");
  const [filterText, setFilterText] = useState("");
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(DEFAULT_COLUMNS);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [showActivityFilters, setShowActivityFilters] = useState(false);
  
  // Total ticks available in the time period
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  
  // Activity filters state
  const [minLtq, setMinLtq] = useState("");
  const [minVolume, setMinVolume] = useState("");
  const [minOiChange, setMinOiChange] = useState("");
  const [aggBuyOnly, setAggBuyOnly] = useState(false);
  const [aggSellOnly, setAggSellOnly] = useState(false);
  const [rowLimit, setRowLimit] = useState(50000);
  
  // Smart Filters state
  const [smartFilters, setSmartFilters] = useState(DEFAULT_SMART_FILTERS);

  // Fetch available symbols from database - filter by selected date
  // Returns symbol NAMES only, not token numbers
  const fetchAvailableSymbols = useCallback(async (dateFilter = null) => {
    try {
      // Use start date for filtering symbols
      const dateParam = dateFilter || format(startDate, "yyyy-MM-dd");
      const response = await apiClient.get(`/tick-history/symbols?date=${dateParam}`);
      const symbols = response.data.symbols || [];
      // Filter out any token numbers (pure numbers) - only keep actual symbol names
      const filteredSymbols = symbols.filter(s => s && !/^\d+$/.test(s) && s.length > 0);
      setAvailableSymbols(filteredSymbols);
    } catch (error) {
      console.error("Failed to fetch symbols:", error);
      // Fallback: use instruments from props - filter out tokens without names
      if (instruments && Object.keys(instruments).length > 0) {
        const symbolList = Object.values(instruments)
          .map(i => i.tradingsymbol)
          .filter(s => s && !/^\d+$/.test(s));
        setAvailableSymbols([...new Set(symbolList)].sort());
      }
    }
  }, [instruments, startDate]);

  // Fetch symbols when date changes
  useEffect(() => {
    fetchAvailableSymbols();
  }, [startDate, fetchAvailableSymbols]);

  // Fetch available dates
  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await apiClient.get("/tick-history/dates");
      setAvailableDates(response.data.dates || []);
    } catch (error) {
      console.error("Failed to fetch dates:", error);
    }
  }, []);

  // Fetch history data
  const fetchHistoryData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Combine date and time
      const startDateTime = new Date(startDate);
      const [startHour, startMin] = startTime.split(':').map(Number);
      startDateTime.setHours(startHour, startMin, 0, 0);
      
      const endDateTime = new Date(endDate);
      const [endHour, endMin] = endTime.split(':').map(Number);
      endDateTime.setHours(endHour, endMin, 59, 999);
      
      const requestBody = {
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        symbols: selectedSymbol !== "ALL" ? [selectedSymbol] : null,
        limit: rowLimit,
        // Activity filters
        min_ltq: minLtq ? parseInt(minLtq) : null,
        min_volume: minVolume ? parseInt(minVolume) : null,
        min_oi_change: minOiChange ? parseInt(minOiChange) : null,
        agg_buy_only: aggBuyOnly,
        agg_sell_only: aggSellOnly
      };
      
      const response = await apiClient.post("/tick-history/query", requestBody);
      
      const ticks = response.data.ticks || [];
      const total = response.data.total || ticks.length;
      
      setHistoryData(ticks);
      setLoadedCount(ticks.length);
      setTotalAvailable(total);
      
      const filterInfo = [];
      if (minLtq) filterInfo.push(`LTQ≥${minLtq}`);
      if (minVolume) filterInfo.push(`Vol≥${minVolume}`);
      if (minOiChange) filterInfo.push(`OI Chg≥${minOiChange}`);
      if (aggBuyOnly) filterInfo.push('Agg Buy');
      if (aggSellOnly) filterInfo.push('Agg Sell');
      
      const filterStr = filterInfo.length > 0 ? ` (${filterInfo.join(', ')})` : '';
      toast.success(`Loaded ${ticks.length.toLocaleString()} / ${total.toLocaleString()} ticks${filterStr}`);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history data");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, startTime, endTime, selectedSymbol, rowLimit, minLtq, minVolume, minOiChange, aggBuyOnly, aggSellOnly]);

  // Load ALL ticks with progress (like PopOut Full Day)
  const loadFullHistory = useCallback(async () => {
    if (isLoadingFull) return;
    setIsLoadingFull(true);
    
    try {
      // Combine date and time
      const startDateTime = new Date(startDate);
      const [startHour, startMin] = startTime.split(':').map(Number);
      startDateTime.setHours(startHour, startMin, 0, 0);
      
      const endDateTime = new Date(endDate);
      const [endHour, endMin] = endTime.split(':').map(Number);
      endDateTime.setHours(endHour, endMin, 59, 999);
      
      let allTicks = [];
      let skip = 0;
      const batchSize = 10000;
      let hasMore = true;
      let total = 0;
      let lastUpdateTime = Date.now();
      
      while (hasMore) {
        const requestBody = {
          start_date: startDateTime.toISOString(),
          end_date: endDateTime.toISOString(),
          symbols: selectedSymbol !== "ALL" ? [selectedSymbol] : null,
          limit: batchSize,
          skip: skip,
          // Activity filters
          min_ltq: minLtq ? parseInt(minLtq) : null,
          min_volume: minVolume ? parseInt(minVolume) : null,
          min_oi_change: minOiChange ? parseInt(minOiChange) : null,
          agg_buy_only: aggBuyOnly,
          agg_sell_only: aggSellOnly
        };
        
        const response = await apiClient.post("/tick-history/query", requestBody);
        const ticks = response.data.ticks || [];
        total = response.data.total || total;
        
        if (ticks.length > 0) {
          allTicks = allTicks.concat(ticks); // concat is more efficient for large arrays
          skip += ticks.length;
          
          // Update UI only every 500ms to prevent stack overflow from too many renders
          const now = Date.now();
          if (now - lastUpdateTime > 500 || !response.data.has_more) {
            setLoadedCount(allTicks.length);
            setTotalAvailable(total);
            lastUpdateTime = now;
          }
          
          // Check if more data available
          hasMore = ticks.length === batchSize && allTicks.length < total;
          
          // Small delay to allow UI update and prevent blocking
          await new Promise(r => setTimeout(r, 50));
        } else {
          hasMore = false;
        }
      }
      
      // Final update with all data
      setHistoryData(allTicks);
      setLoadedCount(allTicks.length);
      setTotalAvailable(total);
      
      toast.success(`Loaded ALL ${allTicks.length.toLocaleString()} ticks`);
    } catch (error) {
      console.error("Failed to load full history:", error);
      toast.error("Failed to load full history");
    } finally {
      setIsLoadingFull(false);
    }
  }, [startDate, endDate, startTime, endTime, selectedSymbol, minLtq, minVolume, minOiChange, aggBuyOnly, aggSellOnly]);

  // Clear activity filters
  const clearActivityFilters = () => {
    setMinLtq("");
    setMinVolume("");
    setMinOiChange("");
    setAggBuyOnly(false);
    setAggSellOnly(false);
  };

  // Check if any activity filter is active
  const hasActiveFilters = minLtq || minVolume || minOiChange || aggBuyOnly || aggSellOnly;

  // Save current tick data to history
  const saveTickHistory = async () => {
    try {
      const response = await apiClient.post("/tick-history/save");
      if (response.data.success) {
        toast.success(`Saved ${response.data.saved_count} ticks to history`);
        fetchAvailableDates();
      } else {
        toast.error("Failed to save history");
      }
    } catch (error) {
      toast.error("Failed to save history");
    }
  };

  // Cleanup old history
  const cleanupHistory = async () => {
    try {
      const response = await apiClient.delete("/tick-history/cleanup?days_to_keep=10");
      toast.success(`Deleted ${response.data.deleted_count} old records`);
      fetchAvailableDates();
    } catch (error) {
      toast.error("Failed to cleanup history");
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (historyData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Symbol", "LTP", "Agg Buy", "Agg Sell", "Volume", "Bid", "Ask", "LTQ", "OI Change", "Timestamp"];
    const rows = historyData.map(tick => [
      tick.symbol,
      tick.last_price,
      tick.agg_buy || 0,
      tick.agg_sell || 0,
      tick.volume_traded || 0,
      tick.bid_price || 0,
      tick.ask_price || 0,
      tick.last_traded_quantity || 0,
      tick.oi_change || 0,
      tick.timestamp
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tick_history_${formatDate(startDate)}_to_${formatDate(endDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  // Initial load
  useEffect(() => {
    fetchAvailableDates();
    fetchAvailableSymbols();
  }, [fetchAvailableDates, fetchAvailableSymbols]);

  // Visible columns
  const visibleColumns = useMemo(() => 
    ALL_COLUMNS.filter(col => visibleColumnKeys.includes(col.key)),
    [visibleColumnKeys]
  );

  // Calculate max values for bar gradients - OPTIMIZED for large datasets
  const maxValues = useMemo(() => {
    if (!historyData.length) return {};
    
    // For large datasets, sample to avoid performance issues
    const sampleSize = Math.min(historyData.length, 10000);
    const sample = historyData.length > sampleSize 
      ? historyData.filter((_, i) => i % Math.ceil(historyData.length / sampleSize) === 0)
      : historyData;
    
    // Use reduce instead of spread operator to avoid stack overflow
    let maxBidQty = 0, maxAskQty = 0, maxAggBuy = 0, maxAggSell = 0;
    let maxLtq = 0, maxVolume = 0, maxOi = 0;
    let maxVolChange = 0, maxOiChange = 0;
    
    for (let i = 0; i < sample.length; i++) {
      const t = sample[i];
      if ((t.bid_qty || 0) > maxBidQty) maxBidQty = t.bid_qty;
      if ((t.ask_qty || 0) > maxAskQty) maxAskQty = t.ask_qty;
      if ((t.agg_buy || 0) > maxAggBuy) maxAggBuy = t.agg_buy;
      if ((t.agg_sell || 0) > maxAggSell) maxAggSell = t.agg_sell;
      if ((t.last_traded_quantity || 0) > maxLtq) maxLtq = t.last_traded_quantity;
      if ((t.volume_traded || 0) > maxVolume) maxVolume = t.volume_traded;
      if ((t.oi || 0) > maxOi) maxOi = t.oi;
      // For volume_change, oi_change - use absolute values
      if (Math.abs(t.volume_change || 0) > maxVolChange) maxVolChange = Math.abs(t.volume_change);
      if (Math.abs(t.oi_change || 0) > maxOiChange) maxOiChange = Math.abs(t.oi_change);
    }
    
    return {
      bid_qty: maxBidQty || 1,
      ask_qty: maxAskQty || 1,
      agg_buy: maxAggBuy || 1,
      agg_sell: maxAggSell || 1,
      last_traded_quantity: maxLtq || 1,
      volume_traded: maxVolume || 1,
      oi: maxOi || 1,
      volume_change: maxVolChange || 1,
      oi_change: maxOiChange || 1,
    };
  }, [historyData]);

  // Sort and filter data
  // Memoize buy/sell counts to avoid recalculating on every render
  const buySellCounts = useMemo(() => {
    if (!historyData.length) return { buys: 0, sells: 0 };
    
    let buys = 0, sells = 0;
    for (let i = 0; i < historyData.length; i++) {
      if (historyData[i].agg_buy > 0) buys++;
      if (historyData[i].agg_sell > 0) sells++;
    }
    return { buys, sells };
  }, [historyData]);

  const sortedAndFilteredData = useMemo(() => {
    // For very large datasets, avoid unnecessary copying
    let data = historyData;
    
    // Filter by symbol text if provided
    if (filterText) {
      const upperFilter = filterText.toUpperCase();
      data = historyData.filter(t => t.symbol?.toUpperCase().includes(upperFilter));
    }
    
    // Sort - create copy only when sorting to avoid mutating original
    if (sortConfig.key) {
      // Only create copy if we haven't already (from filtering)
      if (data === historyData) {
        data = historyData.slice(); // More efficient than spread for large arrays
      }
      
      data.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle nulls
        if (aVal === null || aVal === undefined) aVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
        if (bVal === null || bVal === undefined) bVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
        
        // String comparison for symbol and date
        if (sortConfig.key === 'symbol' || sortConfig.key === 'date') {
          return sortConfig.direction === 'asc' 
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        }
        
        // Numeric comparison
        if (sortConfig.direction === 'asc') {
          return aVal - bVal;
        }
        return bVal - aVal;
      });
    }
    
    // Apply smart filters
    if (smartFilters.enabled) {
      const smartMaxValues = calculateMaxValues(data);
      data = applySmartFilters(data, smartFilters, smartMaxValues);
      
      // If filter mode, only show matching rows
      if (smartFilters.mode === 'filter') {
        data = data.filter(t => t._smartFilterMatch);
      }
    }
    
    return data;
  }, [historyData, filterText, sortConfig, smartFilters]);

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Toggle column visibility
  const toggleColumn = (key) => {
    setVisibleColumnKeys(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // Quick date selections
  const setDateRange = (days) => {
    setEndDate(new Date());
    setStartDate(subDays(new Date(), days));
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0A0A]" data-testid="history-tab">
      {/* Controls Bar */}
      <div className="p-4 border-b border-[#27272A] bg-[#111111] space-y-3">
        <div className="flex items-center gap-4">
          {/* Quick Date Selectors */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#71717A]">Quick:</span>
            <Button variant="ghost" size="sm" onClick={() => setDateRange(1)} className="h-7 text-xs">
              1 Day
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDateRange(3)} className="h-7 text-xs">
              3 Days
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDateRange(5)} className="h-7 text-xs">
              5 Days
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDateRange(10)} className="h-7 text-xs">
              10 Days
            </Button>
          </div>

          <div className="h-6 w-px bg-[#27272A]" />

          {/* Start Date + Time */}
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 bg-[#0A0A0A] border-[#27272A] text-white">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  From: {formatDate(startDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#111111] border-[#27272A]" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => date && setStartDate(date)}
                  disabled={(date) => date > new Date()}
                  className="rounded-md"
                />
              </PopoverContent>
            </Popover>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="h-9 w-24 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
            />
          </div>

          {/* End Date + Time */}
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 bg-[#0A0A0A] border-[#27272A] text-white">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  To: {formatDate(endDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#111111] border-[#27272A]" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => date && setEndDate(date)}
                  disabled={(date) => date > new Date()}
                  className="rounded-md"
                />
              </PopoverContent>
            </Popover>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="h-9 w-24 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
            />
          </div>

          {/* Symbol Filter */}
          <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
            <SelectTrigger className="w-40 h-9 bg-[#0A0A0A] border-[#27272A] text-white">
              <SelectValue placeholder="All Symbols" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Symbols</SelectItem>
              {availableSymbols.map(symbol => (
                <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Activity Filters Toggle */}
          <Popover open={showActivityFilters} onOpenChange={setShowActivityFilters}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className={`h-9 ${hasActiveFilters ? 'bg-blue-900/30 border-blue-500' : 'bg-[#0A0A0A] border-[#27272A]'} text-white`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Activity Scan {hasActiveFilters && `(${[minLtq, minVolume, minOiChange, aggBuyOnly, aggSellOnly].filter(Boolean).length})`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 bg-[#111111] border-[#27272A] p-3" align="start">
              <div className="text-sm font-medium text-white mb-3 flex items-center justify-between">
                <span>Activity Filters</span>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-red-400" onClick={clearActivityFilters}>
                    Clear All
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#71717A] block mb-1">Min LTQ (Last Traded Qty)</label>
                  <Input
                    type="number"
                    value={minLtq}
                    onChange={(e) => setMinLtq(e.target.value)}
                    placeholder="e.g. 1000"
                    className="h-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#71717A] block mb-1">Min Volume</label>
                  <Input
                    type="number"
                    value={minVolume}
                    onChange={(e) => setMinVolume(e.target.value)}
                    placeholder="e.g. 100000"
                    className="h-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#71717A] block mb-1">Min OI Change</label>
                  <Input
                    type="number"
                    value={minOiChange}
                    onChange={(e) => setMinOiChange(e.target.value)}
                    placeholder="e.g. 5000"
                    className="h-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="agg-buy-only"
                      checked={aggBuyOnly}
                      onCheckedChange={setAggBuyOnly}
                      className="border-green-500"
                    />
                    <label htmlFor="agg-buy-only" className="text-xs text-green-400 cursor-pointer">
                      Agg Buy Only
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="agg-sell-only"
                      checked={aggSellOnly}
                      onCheckedChange={setAggSellOnly}
                      className="border-red-500"
                    />
                    <label htmlFor="agg-sell-only" className="text-xs text-red-400 cursor-pointer">
                      Agg Sell Only
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#71717A] block mb-1">Initial Load Limit</label>
                  <Select value={rowLimit.toString()} onValueChange={(v) => setRowLimit(parseInt(v))}>
                    <SelectTrigger className="h-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10000">10,000</SelectItem>
                      <SelectItem value="25000">25,000</SelectItem>
                      <SelectItem value="50000">50,000</SelectItem>
                      <SelectItem value="100000">100,000</SelectItem>
                      <SelectItem value="500000">500,000</SelectItem>
                      <SelectItem value="1000000">1,000,000 (All)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[9px] text-[#52525B] mt-1">Use "Load Full" button to load ALL ticks</p>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Smart Filters */}
          <SmartFiltersPanel filters={smartFilters} setFilters={setSmartFilters} />

          {/* Search Button */}
          <Button onClick={fetchHistoryData} disabled={isLoading || isLoadingFull} className="bg-blue-600 hover:bg-blue-700">
            {isLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Load
          </Button>

          {/* Load Full Button */}
          <Button 
            onClick={loadFullHistory} 
            disabled={isLoading || isLoadingFull}
            variant="outline"
            className="bg-yellow-900/30 border-yellow-600/50 text-yellow-400 hover:bg-yellow-900/50 hover:text-yellow-300"
          >
            {isLoadingFull ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {loadedCount.toLocaleString()}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Full
              </>
            )}
          </Button>
        </div>

        {/* Second Row: Filter, Stats & Actions */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by symbol..."
              className="pl-10 h-8 w-44 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
              data-testid="history-filter"
            />
          </div>
          
          {/* Loaded vs Total Count */}
          <div className="flex items-center gap-2 px-2 py-1 bg-[#1a1a1a] rounded border border-[#27272A]">
            <span className="text-[11px] text-[#71717A]">Loaded:</span>
            <span className="text-xs font-bold text-white">{loadedCount.toLocaleString()}</span>
            {totalAvailable > 0 && totalAvailable > loadedCount && (
              <>
                <span className="text-[11px] text-[#52525B]">/</span>
                <span className="text-xs font-medium text-cyan-400">{totalAvailable.toLocaleString()}</span>
                <span className="text-[9px] text-yellow-400">({Math.round((loadedCount / totalAvailable) * 100)}%)</span>
              </>
            )}
            {totalAvailable > 0 && totalAvailable === loadedCount && (
              <span className="text-[9px] text-green-400">✓</span>
            )}
          </div>
          
          <span className="text-[11px] text-[#71717A]">
            Buys: <span className="text-green-400 font-medium">{buySellCounts.buys.toLocaleString()}</span>
          </span>
          <span className="text-[11px] text-[#71717A]">
            Sells: <span className="text-red-400 font-medium">{buySellCounts.sells.toLocaleString()}</span>
          </span>

          {/* Column Settings */}
          <Popover open={showColumnSettings} onOpenChange={setShowColumnSettings}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-2 bg-[#0A0A0A] border-[#27272A] text-white text-xs">
                <Settings2 className="w-3 h-3 mr-1" />
                Cols
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 bg-[#111111] border-[#27272A] p-2" align="end">
              <div className="text-sm font-medium text-white mb-2">Visible Columns</div>
              <ScrollArea className="h-64">
                {ALL_COLUMNS.map(col => (
                  <div key={col.key} className="flex items-center space-x-2 py-1">
                    <Checkbox
                      id={`col-${col.key}`}
                      checked={visibleColumnKeys.includes(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                      className="border-[#27272A]"
                    />
                    <label htmlFor={`col-${col.key}`} className="text-xs text-[#A1A1AA] cursor-pointer">
                      {col.label}
                    </label>
                  </div>
                ))}
              </ScrollArea>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-xs"
                onClick={() => setVisibleColumnKeys(DEFAULT_COLUMNS)}
              >
                Reset to Default
              </Button>
            </PopoverContent>
          </Popover>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Export CSV */}
          <Button variant="outline" size="sm" onClick={exportToCSV} className="h-7 px-3 bg-green-900/30 border-green-600/50 text-green-400 hover:bg-green-900/50">
            <Download className="w-3 h-3 mr-1" />
            Export CSV
          </Button>

          {/* Save Current Ticks */}
          <Button variant="outline" size="sm" onClick={saveTickHistory} className="h-7 px-3 bg-[#0A0A0A] border-[#27272A]">
            <RefreshCw className="w-3 h-3 mr-1" />
            Save
          </Button>

          {/* Available Dates Info */}
          {availableDates.length > 0 && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#52525B]" />
              <span className="text-[10px] text-[#71717A]">
                {availableDates.length}d history
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Virtualized Data Table */}
      <VirtualizedHistoryTable 
        data={sortedAndFilteredData}
        visibleColumns={visibleColumns}
        maxValues={maxValues}
        sortConfig={sortConfig}
        handleSort={handleSort}
        buySellCounts={buySellCounts}
        smartFilters={smartFilters}
      />
    </div>
  );
}

// Virtualized Table Component for super-fast scrolling with 100K+ rows
function VirtualizedHistoryTable({ data, visibleColumns, maxValues, sortConfig, handleSort, buySellCounts = { buys: 0, sells: 0 }, smartFilters = DEFAULT_SMART_FILTERS }) {
  const parentRef = useRef(null);
  
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 28, // Row height in pixels
    overscan: 20, // Render extra rows for smoother scrolling
  });

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <CalendarDays className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
          <p className="text-[#71717A] text-lg mb-2">No history data</p>
          <p className="text-[#52525B] text-sm">Select a date range and click "Load History" to view tick data</p>
          <p className="text-[#52525B] text-xs mt-2">History is stored for up to 10 days</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="bg-[#111111] border-b border-[#27272A]">
        <div className="flex text-xs font-semibold text-[#71717A] uppercase tracking-wider">
          {visibleColumns.map(col => (
            <div 
              key={col.key}
              className={`py-2 px-1 cursor-pointer hover:bg-[#1a1a1a] flex items-center gap-1 ${
                col.key === 'agg_buy' ? 'bg-green-900/20' : 
                col.key === 'agg_sell' ? 'bg-red-900/20' : ''
              }`}
              style={{ width: col.width, minWidth: col.width, flexShrink: 0 }}
              onClick={() => col.sortable && handleSort(col.key)}
            >
              {col.label}
              {col.sortable && (
                <span className="text-[#52525B]">
                  {sortConfig.key === col.key ? (
                    sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  ) : (
                    <ArrowUpDown className="w-3 h-3" />
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Virtualized Body */}
      <div 
        ref={parentRef}
        className="flex-1 overflow-auto"
        style={{ contain: 'strict' }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const tick = data[virtualRow.index];
            const smartFilterClass = smartFilters.enabled ? getSmartFilterRowClass(tick, smartFilters) : '';
            return (
              <div
                key={virtualRow.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <HistoryRow 
                  tick={tick} 
                  visibleColumns={visibleColumns}
                  maxValues={maxValues}
                  smartFilterClass={smartFilterClass}
                />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Row count indicator */}
      <div className="px-3 py-1.5 bg-[#0A0A0A] border-t border-[#27272A] flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#71717A]">
            Showing <span className="text-white font-bold">{data.length.toLocaleString()}</span> rows
          </span>
          <span className="text-xs text-green-400">
            Buys: {buySellCounts.buys.toLocaleString()}
          </span>
          <span className="text-xs text-red-400">
            Sells: {buySellCounts.sells.toLocaleString()}
          </span>
        </div>
        <span className="text-[10px] text-[#52525B]">Virtual scroll enabled • Scroll for more</span>
      </div>
    </div>
  );
}
